import { collection } from '@debbie/common/dist/index.js'
import { LanguageMap } from '@debbie/common/dist/lang/language-maps/index.js'
import * as db from '@debbie/common/dist/query/db.js'
import {
  EntityName,
  Property,
  Query,
  QueryResult,
  ToTuple,
  fromTuple,
  toTuple,
} from '@debbie/common/dist/query/index.js'
import { DataType } from '@debbie/common/dist/query/types.js'
import { useQuery } from '@debbie/cortex/dist/query/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { Money } from '@debbie/iris/dist/components/index.js'
import RemoteTable from '@debbie/iris/dist/components/RemoteTable.js'
import { sum } from '@debbie/iris/dist/utils/index.js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import { CustomComponent, DisplayComponent } from './DisplayComponents.js'

type ValidProperty<
  Base extends EntityName,
  Q extends Query<typeof db.schema, Base>,
> = {
  [Table in keyof Q['properties']]: {
    // @ts-expect-error
    [Field in Q['properties'][Table]['fields'][number]]: `${Table}.${Field}`
    // @ts-expect-error
  }[Q['properties'][Table]['fields'][number]]
}[keyof Q['properties']]

export type DisplayOptions<
  Base extends EntityName,
  Q extends Query<typeof db.schema, Base>,
> = {
  view: ((
    | {
        value: ValidProperty<Base, Q>
      }
    | {
        component: React.ComponentType<{
          value: QueryResult<typeof db.schema, Base, Q>[number]
        }>
        values: ValidProperty<Base, Q>[]
      }
  ) & {
    label: (lang: LanguageMap) => string
    width?: string
  })[]
}

type Props<Base extends EntityName, Q extends Query<typeof db.schema, Base>> = {
  base: Base
  query: Q
  options: NoInfer<DisplayOptions<Base, Q>>
  emptyComponent?: React.ComponentType<Record<string, never>>
  onClick?: (item: QueryResult<typeof db.schema, Base, Q>[number]) => void
  onSortChange?: (sort: {
    key: string | undefined
    direction: 'ASC' | 'DESC' | undefined
  }) => void
}

export const options =
  <Base extends EntityName, Q extends Query<typeof db.schema, Base>>(
    query: Q,
  ) =>
  (o: DisplayOptions<Base, Q>) => {
    return o
  }

type Filter<
  Base extends EntityName,
  Q extends Query<typeof db.schema, Base>,
> = {
  orderBy: Q['orderBy']
  orderByDirection?: 'ASC' | 'DESC'
}

const Item = styled.div``
const Collapse = styled.span`
  display: block;
  font-size: 0.8rem;
  cursor: pointer;
`

const Items = (props: {
  Comp: React.ComponentType<{ value: any }>
  data: any[]
  money: boolean
}) => {
  const lang = useSelector((s: RootState) => s.lang)
  const { data, Comp } = props
  const [open, setOpen] = useState(false)

  if (open) {
    return (
      <>
        {data.map((d) => (
          <Item>{React.createElement(Comp, { value: d })}</Item>
        ))}
        <Collapse
          onClick={() => {
            setOpen(false)
          }}
        >
          {lang.views.collapse}
        </Collapse>
        {props.money ? <Money amount={data.reduce(sum, 0)} color /> : null}
      </>
    )
  }

  return (
    <>
      {data.slice(0, 5).map((d) => (
        <Item>{React.createElement(Comp, { value: d })}</Item>
      ))}
      {data.length > 5 ? (
        <Collapse
          onClick={() => {
            setOpen(true)
          }}
        >
          {lang.views.expand}
        </Collapse>
      ) : null}
      {props.money ? <Money amount={data.reduce(sum, 0)} color /> : null}
    </>
  )
}

export const View = <
  Base extends EntityName,
  Q extends Query<typeof db.schema, Base>,
>(
  props: Props<Base, Q>,
) => {
  const lang = useSelector((s: RootState) => s.lang)

  const fields = []
  for (const key in props.query.properties) {
    const table = key as EntityName
    const property = props.query.properties[table]
    if (property != null) {
      for (const field of property.fields) {
        fields.push([table, field] as const)
      }
    }
  }

  const values =
    props.options.view && props.options.view.length > 0
      ? Object.fromEntries(
          props.options.view.map((view, index) => {
            if ('component' in view) {
              return [
                index,
                {
                  label: view.label(lang),
                  width: view.width,
                  sortable: false,
                  map: (_: any, all: any) => {
                    return React.createElement(view.component, { value: all })
                  },
                },
              ]
            }
            const field = toTuple(view.value)
            // @ts-expect-error
            const type = db.schema.entities[field[0]].properties[field[1]]
              .type as DataType

            const Comp =
              CustomComponent[view.value as Property<typeof db.schema>] ??
              DisplayComponent[type.type]

            return [
              index,
              {
                label: view.label(lang),
                width: view.width,
                sortable: props.base === field[0] ? true : false,
                sortValue: field[1],
                map: (__data: any, all: any) => {
                  const data = all[field[0]]
                  if (data == null) {
                    return null
                  }
                  return Array.isArray(data) ? (
                    <Items
                      data={data.map((x) => x[field[1]])}
                      Comp={Comp}
                      money={type.type === 'money'}
                    />
                  ) : (
                    React.createElement(Comp, { value: data[field[1]] })
                  )
                },
              },
            ]
          }),
        )
      : Object.fromEntries(
          fields.map((field) => {
            const fieldKey = fromTuple(
              field as ToTuple<Property<typeof db.schema>>,
            )

            // @ts-expect-error
            const type = db.schema.entities[field[0]].properties[field[1]]
              .type as DataType

            const Comp =
              CustomComponent[fieldKey] ?? DisplayComponent[type.type]

            const value = {
              label: `${field[0]}.${field[1]}`,
              sortable: props.base === field[0] ? true : false,
              sortValue: field[1],
              map: (__data: any, all: any) => {
                const data = all[field[0]]
                if (data == null) {
                  return null
                }
                return Array.isArray(data) ? (
                  <Items
                    data={data.map((x) => x[field[1]])}
                    Comp={Comp}
                    money={type.type === 'money'}
                  />
                ) : (
                  React.createElement(Comp, {
                    value: data[field[1]],
                  })
                )
              },
            }

            return [`${field[0]}.${field[1]}`, value]
          }),
        )

  return (
    <RemoteTable
      useFetch={(filters, page, pageSize) => {
        const loader = useQuery(props.query.base)({
          ...props.query,
          limit: pageSize,
          offset: page * pageSize,
          orderBy: filters.orderBy ?? props.query.orderBy,
          orderByDirection:
            filters.orderByDirection ?? props.query.orderByDirection,
        })

        return loader
      }}
      pageSize={50}
      initFilters={
        {
          orderBy: props.query.orderBy,
          orderByDirection: props.query.orderByDirection,
        } as Filter<Base, Q>
      }
      genKey={(v) => JSON.stringify(v)} // TODO: Change this shit
      sortKeyKey="orderBy"
      sortDirectionKey="orderByDirection"
      values={values}
      emptyComponent={props.emptyComponent}
      // @ts-expect-error
      onClick={props.onClick}
      onFilterChange={(filters) => {
        console.log(filters)
        if (props.onSortChange) {
          props.onSortChange({
            key: filters.orderBy,
            direction: filters.orderByDirection,
          })
        }
      }}
    />
  )
}
